import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Send, Copy, Crown, MailWarning, BarChart3, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API, formatApiErrorDetail } from "@/context/AuthContext";
import { SalesChart } from "@/components/owner/SalesChart";

const EASE = [0.16, 1, 0.3, 1];
const OWNER_EMAIL = "bathilysamir22@gmail.com";

const PRODUCT_ORDER = ["memorisation", "revision", "pack-complet"];
const PRODUCT_META = {
  memorisation: { title: "Guide de mémorisation — 6 mois", price: 29.99 },
  revision: { title: "Guide de révision — 30 jours", price: 24.99 },
  "pack-complet": { title: "Pack Gold — Mémorisation + révision", price: 44.99 },
};

const daysSince = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));

export default function OwnerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [resending, setResending] = useState("");
  const [autoEnabled, setAutoEnabled] = useState(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/connexion", { replace: true });
  }, [user, navigate]);

  const loadDash = () => {
    axios
      .get(`${API}/vendor/dashboard`, { withCredentials: true })
      .then((r) => setDash(r.data))
      .catch(() => setDash(null));
  };

  useEffect(() => {
    if (user && user.email === OWNER_EMAIL) loadDash();
  }, [user]);

  useEffect(() => {
    if (dash) setAutoEnabled(dash.auto_reminders_enabled);
  }, [dash]);

  const toggleAuto = async () => {
    const next = !autoEnabled;
    setAutoEnabled(next);
    setToggling(true);
    try {
      await axios.post(`${API}/vendor/auto-reminders`, { enabled: next }, { withCredentials: true });
      toast.success(next ? "Relances automatiques activées" : "Relances automatiques désactivées");
    } catch (err) {
      setAutoEnabled(!next);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setToggling(false);
    }
  };

  if (user === null || user === false) {
    return (
      <div className="info-page member-loading">
        <p>Chargement…</p>
      </div>
    );
  }

  if (user.email !== OWNER_EMAIL) {
    return (
      <div className="info-page member-loading" data-testid="owner-denied">
        <p>Cet espace est réservé au propriétaire du site.</p>
        <Link to="/" className="btn">← Retour à l'accueil</Link>
      </div>
    );
  }

  const resend = async (orderId) => {
    setResending(orderId);
    try {
      const r = await axios.post(`${API}/vendor/resend-code`, { order_id: orderId }, { withCredentials: true });
      toast.success(`Code renvoyé à ${r.data.email}`);
      loadDash();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResending("");
    }
  };

  const copyRelance = (o) => {
    const date = o.date ? new Date(o.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) : "récemment";
    const msg = `Assalamu alaykum,\n\nTu as commandé « ${o.product_title} » le ${date} — ton code d'activation ${o.code} t'attend !\n\nPour récupérer ton guide :\n1. Crée ton compte sur le site SamNara avec cette adresse email\n2. Ouvre ton espace membre\n3. Colle ton code — le téléchargement est immédiat\n\nUne question ? Réponds simplement à cet email, on est là pour t'aider.\n\n— SamNara`;
    navigator.clipboard?.writeText(msg);
    toast.success("Message de relance copié !");
  };

  const orders = dash?.orders || [];
  const relances = orders.filter((o) => !o.code_used && o.email_sent);

  return (
    <div className="info-page" data-testid="owner-page">
      <header className="member-header">
        <Link to="/" className="logo logo-link" data-testid="brand-home-link" aria-label="SamNara — accueil">
          <img src="/logo-samnara.png" alt="SamNara" className="logo-img logo-img-lg" />
        </Link>
        <div className="member-head-right">
          <span className="member-email">Espace propriétaire</span>
          <Link to="/espace" className="copy-btn" data-testid="owner-back-member">Mon espace membre</Link>
          <button className="copy-btn" onClick={logout} data-testid="owner-logout-btn">Se déconnecter</button>
        </div>
      </header>

      <motion.div
        className="owner-page-body"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
      >
        <div className="activate-panel owner-panel" data-testid="owner-kpis">
          <div className="activate-head owner-head">
            <Crown size={18} />
            <b>Vue d'ensemble</b>
          </div>
          {!dash ? (
            <p className="dash-empty">Chargement…</p>
          ) : (
            <div className="kpi-row">
              <div className="kpi-card" data-testid="stat-orders"><b>{dash.stats.orders}</b><span>commandes</span></div>
              <div className="kpi-card" data-testid="stat-revenue"><b>{dash.stats.revenue.toLocaleString("fr-FR")} €</b><span>valeur totale</span></div>
              <div className="kpi-card" data-testid="stat-codes-used"><b>{dash.stats.codes_used}</b><span>codes activés</span></div>
              <div className="kpi-card" data-testid="stat-members"><b>{dash.stats.members}</b><span>membres</span></div>
            </div>
          )}
          <button type="button" className="copy-btn dash-refresh" onClick={loadDash} data-testid="owner-refresh-btn">
            <RefreshCw size={14} />
            Actualiser
          </button>
        </div>

        {dash && dash.sales_by_day && (
          <div className="activate-panel owner-panel" data-testid="sales-chart-panel">
            <div className="activate-head owner-head">
              <BarChart3 size={18} />
              <b>Ventes — 30 derniers jours</b>
            </div>
            <SalesChart data={dash.sales_by_day} />
          </div>
        )}

        {dash && (
          <div className="activate-panel owner-panel" data-testid="auto-reminders-panel">
            <div className="auto-toggle-row">
              <div className="auto-toggle-txt">
                <b>
                  <BellRing size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                  Relance automatique
                </b>
                <span>Si un code n'est pas activé, le client reçoit un rappel par email après 48 h, puis un dernier après 7 jours. Les relances envoyées apparaissent dans la liste ci-dessous.</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!autoEnabled}
                aria-label="Activer ou désactiver la relance automatique"
                className={`switch ${autoEnabled ? "on" : ""}`}
                onClick={toggleAuto}
                disabled={toggling || autoEnabled === null}
                data-testid="auto-reminders-toggle"
              >
                <span className="switch-knob" />
              </button>
            </div>
          </div>
        )}

        {dash && relances.length > 0 && (
          <div className="activate-panel owner-panel" data-testid="relances-panel">
            <div className="activate-head owner-head">
              <MailWarning size={18} />
              <b>Relances — codes pas encore utilisés ({relances.length})</b>
            </div>
            <p>Ces clients ont reçu leur code mais ne l'ont pas encore activé. Renvoie-leur le code d'un clic, ou copie un message de relance gentil à leur envoyer.</p>
            <div className="order-list">
              {relances.map((o) => (
                <div className="order-row" key={o.order_id} data-testid={`relance-row-${o.order_id}`}>
                  <div className="order-main">
                    <b>{o.product_title}</b>
                    <span className="order-email">{o.email} — en attente depuis {daysSince(o.date)} j</span>
                  </div>
                  <div className="order-meta">
                    <span className="order-code">{o.code}</span>
                    {o.reminders_count > 0 && (
                      <span className="pill auto" data-testid={`reminder-badge-${o.order_id}`}>
                        relance auto ×{o.reminders_count}
                      </span>
                    )}
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={() => resend(o.order_id)}
                      disabled={resending === o.order_id}
                      data-testid={`resend-btn-${o.order_id}`}
                    >
                      <Send size={13} />
                      {resending === o.order_id ? "Envoi…" : "Renvoyer le code"}
                    </button>
                    <button type="button" className="copy-btn" onClick={() => copyRelance(o)} data-testid={`copy-relance-${o.order_id}`}>
                      <Copy size={13} />
                      Copier la relance
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {PRODUCT_ORDER.map((key) => {
          const list = orders.filter((o) => o.product === key);
          const meta = PRODUCT_META[key];
          const subtotal = list.reduce((sum, o) => sum + (o.price || 0), 0);
          return (
            <div className="activate-panel owner-panel" key={key} data-testid={`product-section-${key}`}>
              <div className="prod-sec-head">
                <b>{meta.title}</b>
                <span className="prod-sec-meta">
                  {list.length} commande{list.length > 1 ? "s" : ""} · {subtotal.toLocaleString("fr-FR")} €
                </span>
              </div>
              {list.length === 0 ? (
                <p className="dash-empty">Aucune commande pour cet article pour le moment.</p>
              ) : (
                <div className="order-list">
                  {list.map((o) => (
                    <div className="order-row" key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                      <div className="order-main">
                        <b>{o.email}</b>
                        <span className="order-email">
                          {o.date ? new Date(o.date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                          {" — "}{Number(o.price).toLocaleString("fr-FR")} {o.currency === "EUR" ? "€" : o.currency}
                        </span>
                      </div>
                      <div className="order-meta">
                        <span className="order-code">{o.code}</span>
                        <span className={`pill ${o.code_used ? "ok" : "wait"}`}>{o.code_used ? "activé" : "en attente"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <Link to="/espace" className="back-link" data-testid="owner-back-link">
          <ArrowLeft size={14} /> Retour à mon espace membre
        </Link>
      </motion.div>
    </div>
  );
}
